/**
 * InlineEditable — span/div contentEditable estilo Canva/Notion.
 *
 * El usuario hace click en el texto del preview y lo edita IN-SITU,
 * sin modal ni form lateral. Se llama `onChange` en blur (no en cada
 * keystroke) — esto es CRÍTICO porque cualquier re-render durante la
 * escritura mata el caret/focus en contenteditable.
 *
 * Detalles de implementación que te van a morder si los cambias:
 *
 * 1. NO usamos React `value` para sincronizar el DOM mientras está activo.
 *    Solo seteamos el contenido inicial via `dangerouslySetInnerHTML` /
 *    ref-mount, y dejamos que el browser maneje el caret. Cuando cambia
 *    el `value` desde fuera (e.g. otra parte del editor mutó la sección)
 *    actualizamos sólo si el campo NO está enfocado.
 *
 * 2. Para multiline aceptamos saltos de línea reales. `innerText` los
 *    devuelve correctamente; `innerHTML` los envuelve en <br>/<div>.
 *    Por eso leemos siempre `innerText` en blur.
 *
 * 3. `Enter` en single-line confirma (blur). `Shift+Enter` permite
 *    nueva línea en multiline. `Escape` revierte y suelta el focus.
 */
import {
  useCallback,
  useEffect,
  useRef,
  type CSSProperties,
  type KeyboardEvent as ReactKeyboardEvent,
} from "react";
import { fieldStyleToCss, useCurrentSectionId, useFieldStyle } from "./EditorStyleContext";

interface InlineEditableProps {
  value: string;
  onChange: (next: string) => void;
  multiline?: boolean;
  placeholder?: string;
  className?: string;
  /** "span" para inline (default), "div" para bloque. */
  as?: "span" | "div";
  /** Si true, se renderiza como texto plano sin contenteditable. */
  readonly?: boolean;
  style?: CSSProperties;
  /** Callback cuando el campo recibe focus (usado para mostrar TextStylePopover). */
  onFieldFocus?: (el: HTMLElement) => void;
  /** Callback cuando pierde focus (con tolerance que el padre maneje). */
  onFieldBlur?: (el: HTMLElement) => void;
  /** Identificador estable para persistir estilos en `section.styles[fieldPath]`. */
  fieldPath?: string;
  /** Id de la sección a la que pertenece (necesario junto con fieldPath). */
  sectionId?: string;
}

export function InlineEditable({
  value,
  onChange,
  multiline = false,
  placeholder = "",
  className,
  as = "span",
  readonly = false,
  style,
  onFieldFocus,
  onFieldBlur,
  fieldPath,
  sectionId,
}: InlineEditableProps) {
  // Estilos persistidos del campo (si hay sectionId+fieldPath y EditorStyleProvider).
  // El sectionId se puede pasar por prop o leer del SectionIdContext (propagado
  // automáticamente por SectionView).
  const ctxSectionId = useCurrentSectionId();
  const effectiveSectionId = sectionId || ctxSectionId || undefined;
  const persistedStyle = useFieldStyle(effectiveSectionId, fieldPath);
  const mergedStyle: CSSProperties = { ...style, ...fieldStyleToCss(persistedStyle) };
  const ref = useRef<HTMLElement | null>(null);
  // El último valor que metimos en el DOM. Sirve para detectar si el caller
  // pasó un nuevo `value` (refresh externo) y debemos sobrescribir.
  const lastDomValue = useRef<string>(value || "");
  // Si el caret está dentro, no tocamos el DOM (rompería la edición).
  const focused = useRef<boolean>(false);

  // Mount: inyectar el value inicial preservando \n como <br> en multiline.
  useEffect(() => {
    const el = ref.current;
    if (!el) return;
    const text = value || "";
    if (focused.current) return; // edición en curso → no tocar
    if (lastDomValue.current === text && el.textContent === text) return;
    if (multiline) {
      // Para multi: <br> visual + \n en innerText
      el.innerHTML = escapeHtml(text).replace(/\n/g, "<br>");
    } else {
      el.textContent = text;
    }
    lastDomValue.current = text;
  }, [value, multiline]);

  const commit = useCallback(() => {
    const el = ref.current;
    if (!el) return;
    let next = (el.innerText || "").replace(/ /g, " ");
    if (!multiline) next = next.replace(/\n+/g, " ");
    next = next.trim();
    if (next === (value || "")) return;
    lastDomValue.current = next;
    onChange(next);
  }, [multiline, onChange, value]);

  const onBlur = useCallback(() => {
    focused.current = false;
    commit();
    if (ref.current && onFieldBlur) onFieldBlur(ref.current);
  }, [commit, onFieldBlur]);

  const onFocus = useCallback(() => {
    focused.current = true;
    if (ref.current && onFieldFocus) onFieldFocus(ref.current);
  }, [onFieldFocus]);

  const onKeyDown = useCallback(
    (e: ReactKeyboardEvent<HTMLElement>) => {
      if (e.key === "Enter") {
        if (multiline && e.shiftKey) {
          // permitir nueva línea
          return;
        }
        if (!multiline) {
          e.preventDefault();
          (e.target as HTMLElement).blur();
          return;
        }
        if (multiline && !e.shiftKey) {
          // Enter solo: confirma (no nueva línea) — equivalente a blur
          e.preventDefault();
          (e.target as HTMLElement).blur();
          return;
        }
      }
      if (e.key === "Escape") {
        e.preventDefault();
        // Revertir
        const el = e.target as HTMLElement;
        if (multiline) {
          el.innerHTML = escapeHtml(value || "").replace(/\n/g, "<br>");
        } else {
          el.textContent = value || "";
        }
        el.blur();
      }
    },
    [multiline, value],
  );

  const onPaste = useCallback(
    (e: React.ClipboardEvent<HTMLElement>) => {
      // Pegar como texto plano (evita estilos/imágenes del clipboard)
      e.preventDefault();
      const text = e.clipboardData.getData("text/plain") || "";
      const cleaned = multiline ? text : text.replace(/\n+/g, " ");
      document.execCommand("insertText", false, cleaned);
    },
    [multiline],
  );

  if (readonly) {
    const Tag = as as any;
    return (
      <Tag className={className} style={mergedStyle}>
        {multiline ? value.split("\n").map((line, i) => <span key={i}>{line}<br/></span>) : value}
      </Tag>
    );
  }

  const cls = ["al-edit", multiline ? "al-edit-multi" : "", className].filter(Boolean).join(" ");
  const Tag = as as any;
  const isEmpty = !value;

  return (
    <Tag
      ref={ref as any}
      className={cls}
      contentEditable
      suppressContentEditableWarning
      spellCheck={false}
      onBlur={onBlur}
      onFocus={onFocus}
      onKeyDown={onKeyDown}
      onPaste={onPaste}
      data-placeholder={placeholder}
      data-empty={isEmpty ? "1" : ""}
      data-fld={fieldPath}
      data-sid={effectiveSectionId}
      style={mergedStyle}
    />
  );
}

function escapeHtml(s: string): string {
  return String(s == null ? "" : s).replace(/[&<>"']/g, (c) =>
    ({ "&": "&amp;", "<": "&lt;", ">": "&gt;", '"': "&quot;", "'": "&#39;" } as Record<string, string>)[c],
  );
}
